// MCIP-19 — pure logic behind the incident banner + panel (no DOM, node-testable).
//
// The API (/api/incidents) already groups pod-wise and sorts by recency; this
// module owns the presentation decisions: when the red banner shows, what it
// says, and how a cluster row deep-links into the existing dashboard state.

/**
 * Banner condition + label. Null means NO banner (no active groups — the
 * banner must never render an empty scare strip). Counting groups, not
 * clusters: one pod-wise incident group is one incident to act on.
 */
export function bannerLabel(incidents, { region = null, environment = null } = {}) {
  const n = incidents?.groups?.length ?? 0;
  if (n === 0) return null;
  // The window is part of the number (Aug 11 review: the banner counted 7d
  // while the screen defaulted to 72h — two honest numbers that LOOKED like
  // one bug). Days derive from the payload itself, never hardcoded.
  const days = Math.round((incidents.window_hours || 168) / 24);
  // The slice is part of the number too: the count was fetched for the active
  // region/environment, and the label must say which one it counted.
  const slice = [region ? String(region).toUpperCase() : null, environment || null]
    .filter(Boolean).join('/');
  const where = slice ? ` in ${slice}` : '';
  return `⛔ ${n} pod${n === 1 ? '' : 's'} with backend incidents${where} in the last ${days}d — open incidents`;
}

/**
 * The existing deep-link the rest of the app already honours (?job=…&cluster=…
 * expands the row in place). A cluster can span jobs — the first affected job
 * is the entry point; the cluster row lists the rest.
 */
export function clusterDeepLink(cluster) {
  const job = cluster.affected_jobs?.[0];
  if (!job || !cluster.cluster_id) return null;
  const q = new URLSearchParams({ job, cluster: cluster.cluster_id });
  return `?${q.toString()}`;
}

/** "3 clusters · 9 failures · 2 jobs" — the group header's totals line. */
export function totalsLabel(totals) {
  if (!totals) return '';
  const part = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
  return [
    part(totals.clusters ?? 0, 'cluster'),
    part(totals.failures ?? 0, 'failure'),
    part(totals.jobs ?? 0, 'job'),
  ].join(' · ');
}

// ---------------------------------------------------------------------------
// MCIP-22 — the incident-response screen's pure logic.

/** Four windows: three response horizons + 30d archaeology (per-window
 *  witness stats make wide windows exactly as accurate as narrow ones).
 *  The server clamps at 720h; longer belongs to trends. */
export const INCIDENT_WINDOWS = [
  { label: '24h', hours: 24 },
  { label: '72h', hours: 72 },
  { label: '7d', hours: 168 },
  { label: '30d', hours: 720 },
];

/** A valid chip value or the default (72h) — deep links can't invent windows. */
export function clampWindowChip(hours) {
  const n = Number(hours);
  return INCIDENT_WINDOWS.some((w) => w.hours === n) ? n : 72;
}

/** "2h ago" / "yesterday" / "4 days ago" — the group list's recency line. */
export function relTime(iso, now = Date.now()) {
  const t = Date.parse(iso || '');
  if (!Number.isFinite(t)) return '—';
  const mins = Math.max(0, Math.round((now - t) / 60_000));
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}

/**
 * Absolute job-view deep link (`/?job=…&cluster=…`) — the incidents screen
 * lives on /incidents, so a RELATIVE ?query link would stay on that path and
 * point at nothing. Same target state clusterDeepLink produces for the panel.
 */
export function jobViewLink(jobId, clusterId = null) {
  if (!jobId) return null;
  const q = new URLSearchParams({ job: jobId });
  if (clusterId) q.set('cluster', clusterId);
  return `/?${q.toString()}`;
}

/**
 * Per-job witness rows for a cluster: the API's `jobs` breakdown (origin
 * first, per-job failure counts) when present; older payload shapes fall back
 * to the flat id list — with the pooled count on every row being exactly the
 * confusion the breakdown replaced, the fallback marks it as such.
 */
export function jobRows(cluster) {
  if (Array.isArray(cluster?.jobs) && cluster.jobs.length) return cluster.jobs;
  return (cluster?.affected_jobs || []).map((jobId) => ({
    job_id: jobId,
    pod: null,
    failures: cluster.member_count ?? null,
    pooled: true, // count is the cluster total, not this job's share
    last_seen_at: cluster.last_seen_at ?? null,
  }));
}

/**
 * The one-line cross-pod story for a group. Each pod's lens lists ONLY its
 * own job rows (follow-up review: foreign job names inside safety's table
 * read as safety jobs) — the spread is a SUMMARY here instead:
 *   - witness lens: why it's shown here + where it originates,
 *   - origin lens: which other pods are hit (aggregated jobs · failures),
 *   - pure-owner lens: the table shows the witnessing pods' jobs, tagged.
 * Null when the group stays entirely within its own pod.
 */
export function crossPodNote(group) {
  const clusters = group?.clusters || [];
  const foreignOrigins = [...new Set(
    clusters.map((c) => c.origin_pod).filter((p) => p && p !== group.pod)
  )].sort();
  // Aggregated cross-pod spread (witness/origin lenses).
  const spreadByPod = new Map();
  for (const s of clusters.flatMap((c) => c.spread || [])) {
    const agg = spreadByPod.get(s.pod) || { pod: s.pod, jobs: 0, failures: 0 };
    agg.jobs += s.jobs;
    agg.failures += s.failures;
    spreadByPod.set(s.pod, agg);
  }
  if (!foreignOrigins.length && !spreadByPod.size) return null;
  // Owner-without-witnesses: none of this pod's own jobs run the affected
  // tests (every cluster's own row list is empty) — the fault is OWNED here,
  // observed elsewhere.
  const pureOwner = clusters.length > 0 && clusters.every((c) => (c.jobs || []).length === 0);
  const parts = [];
  if (foreignOrigins.length) {
    parts.push(`Shown here because ${group.pod}'s jobs are affected — `
      + `originates in ${foreignOrigins.join(', ')} (also listed there).`);
  }
  if (spreadByPod.size) {
    const spread = [...spreadByPod.values()].sort((a, b) => b.failures - a.failures)
      .map((s) => `${s.pod} (${s.jobs} job${s.jobs === 1 ? '' : 's'} · ${s.failures} failure${s.failures === 1 ? '' : 's'})`);
    parts.push(pureOwner
      ? `${group.pod} owns this fault; it is witnessed by ${spread.join(', ')} — their views list the job rows.`
      : `Also hitting ${spread.join(', ')} — listed in their views; only ${group.pod}'s jobs are listed here.`);
  }
  return parts.join(' ');
}
